import {
  ChangeDetectionStrategy,
  Component,
  input,
  output,
  signal,
  computed,
  effect,
  ElementRef,
  viewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { OutlineParseError } from '../../utils/outline-adapter';
import { MarkdownParser } from '../../utils/jsmind.markdown';
import { FlashMapOutlineTranslations, FLASH_MAP_TRANSLATIONS } from '../../i18n/flash-map-i18n';

export interface TreeNode {
  id: string;
  topic: string;
  children: TreeNode[];
  expanded: boolean;
  lineIndex: number;
}

@Component({
  selector: 'lib-outline-tree-view',
  templateUrl: './outline-tree-view.component.html',
  styleUrls: ['./outline-tree-view.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule],
})
export class OutlineTreeViewComponent {
  readonly data = input<TreeNode[]>([]);
  readonly selectedNodeId = input<string | null>(null);
  readonly error = input<OutlineParseError | null>(null);
  readonly translations = input<FlashMapOutlineTranslations>(FLASH_MAP_TRANSLATIONS.en.outline);
  readonly disabled = input<boolean>(false);
  readonly editNodeId = input<string | null>(null);
  readonly active = input<boolean>(false);

  readonly nodeSelect = output<string>();
  readonly nodeEdit = output<{ id: string; topic: string }>();
  readonly nodeCreate = output<{ parentId: string; topic: string }>();
  readonly nodeDelete = output<string>();
  readonly nodeStartEdit = output<string>();
  readonly nodeIndent = output<string>();
  readonly nodeOutdent = output<string>();
  readonly editFinished = output<void>();

  readonly containerRef = viewChild<ElementRef<HTMLDivElement>>('container');

  constructor() {
    // Efeito para iniciar edição quando editNodeId muda
    effect(() => {
      const nodeIdToEdit = this.editNodeId();
      const isActive = this.active();
      if (isActive && nodeIdToEdit && this.nodeExists(nodeIdToEdit)) {
        setTimeout(() => {
          this.startEdit(nodeIdToEdit);
        }, 50);
      }
    });

    // Efeito para manter foco no container quando item selecionado (para capturar TAB)
    effect(() => {
      const selected = this.selectedNodeId();
      const editing = this.editingNodeId();
      const isActive = this.active();
      if (isActive && selected && !editing) {
        setTimeout(() => {
          const container = this.containerRef()?.nativeElement;
          if (container && document.activeElement !== container) {
            container.focus();
          }
        }, 0);
      }
    });
  }

  private nodeExists(nodeId: string): boolean {
    const checkNode = (nodes: TreeNode[]): boolean => {
      for (const node of nodes) {
        if (node.id === nodeId) return true;
        if (node.children.length > 0 && checkNode(node.children)) return true;
      }
      return false;
    };
    return checkNode(this.data());
  }

  private editingNodeId = signal<string | null>(null);
  private expandedNodesMap = signal<Record<string, boolean>>({});
  private expandedTree = signal<TreeNode[]>([]);
  private readonly nodeHtmlCache = new Map<string, { topic: string; html: string }>();

  readonly flatTree = computed(() => {
    const expanded = this.expandedNodesMap();
    const flat: Array<{ node: TreeNode; depth: number }> = [];
    
    const walk = (nodes: TreeNode[], depth: number): void => {
      nodes.forEach(node => {
        flat.push({ node, depth });
        const isExpanded = expanded[node.id] !== undefined ? expanded[node.id] : true;
        if (isExpanded && node.children.length > 0) {
          walk(node.children, depth + 1);
        }
      });
    };
    
    walk(this.data(), 0);
    return flat;
  });

  isExpanded(nodeId: string): boolean {
    const expanded = this.expandedNodesMap()[nodeId];
    return expanded !== undefined ? expanded : true;
  }

  getNodeHtml(node: TreeNode): string {
    const cached = this.nodeHtmlCache.get(node.id);
    if (cached && cached.topic === node.topic) {
      return cached.html;
    }
    const html = MarkdownParser.parse(node.topic);
    this.nodeHtmlCache.set(node.id, { topic: node.topic, html });
    return html;
  }

  toggleExpand(nodeId: string, hasChildren: boolean): void {
    if (!hasChildren) {
      return;
    }
    this.expandedNodesMap.update(expanded => {
      const current = expanded[nodeId];
      const newExpanded = { ...expanded };
      newExpanded[nodeId] = current === undefined ? false : !current;
      return newExpanded;
    });
  }

  selectNode(nodeId: string): void {
    this.nodeSelect.emit(nodeId);
    // Garantir que o container tenha foco para capturar teclas
    setTimeout(() => {
      const container = this.containerRef()?.nativeElement;
      if (container) {
        container.focus();
      }
    }, 0);
  }

  createNode(parentId: string): void {
    if (this.disabled()) {
      return;
    }
    this.nodeCreate.emit({ parentId, topic: this.translations().newItem });
  }

  deleteNode(nodeId: string): void {
    if (this.disabled()) {
      return;
    }
    this.nodeDelete.emit(nodeId);
  }

  onContainerKeydown(event: KeyboardEvent): void {
    if (!this.active() || this.disabled() || this.editingNodeId() !== null) {
      return;
    }

    const selectedId = this.selectedNodeId();
    if (!selectedId) {
      return;
    }

    if (event.key === 'Enter') {
      event.preventDefault();
      // Criar novo irmão do item selecionado
      const parentId = this.getParentId(selectedId);
      if (parentId) {
        this.nodeCreate.emit({ parentId, topic: this.translations().newItem });
      }
    }

    if (event.key === 'Tab' && !event.shiftKey) {
      event.preventDefault();
      // Tornar o item filho do item anterior
      const previousSibling = this.getPreviousSibling(selectedId);
      if (previousSibling) {
        this.nodeIndent.emit(selectedId);
      }
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      // Navegar para o próximo item
      const nextNode = this.getNextNode(selectedId);
      if (nextNode) {
        this.selectNode(nextNode);
      }
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      // Navegar para o item anterior
      const prevNode = this.getPreviousNode(selectedId);
      if (prevNode) {
        this.selectNode(prevNode);
      }
    }

    if (event.key === 'F2') {
      event.preventDefault();
      // Entrar em modo de edição
      this.startEdit(selectedId);
    }

    if (event.key === 'Delete') {
      event.preventDefault();
      // Deletar o item selecionado
      this.deleteNode(selectedId);
    }

    if (event.key === 'Backspace') {
      event.preventDefault();
      // Voltar 1 nível na hierarquia (outdent)
      this.nodeOutdent.emit(selectedId);
    }
  }

  private getNextNode(nodeId: string): string | null {
    const flatNodes = this.flatTree();
    const currentIndex = flatNodes.findIndex(item => item.node.id === nodeId);
    if (currentIndex === -1 || currentIndex >= flatNodes.length - 1) {
      return null;
    }
    return flatNodes[currentIndex + 1].node.id;
  }

  private getPreviousNode(nodeId: string): string | null {
    const flatNodes = this.flatTree();
    const currentIndex = flatNodes.findIndex(item => item.node.id === nodeId);
    if (currentIndex <= 0) {
      return null;
    }
    return flatNodes[currentIndex - 1].node.id;
  }

  private getPreviousSibling(nodeId: string): string | null {
    const flatNodes = this.flatTree();
    const currentIndex = flatNodes.findIndex(item => item.node.id === nodeId);
    if (currentIndex <= 0) return null;

    const currentDepth = flatNodes[currentIndex].depth;
    // Procurar para trás pelo primeiro nó com a mesma profundidade
    for (let i = currentIndex - 1; i >= 0; i--) {
      if (flatNodes[i].depth === currentDepth) {
        return flatNodes[i].node.id;
      }
      if (flatNodes[i].depth < currentDepth) {
        return null;
      }
    }
    return null;
  }

  startEdit(nodeId: string): void {
    this.editingNodeId.set(nodeId);
    // Selecionar o texto após o input ser renderizado
    setTimeout(() => {
      const input = document.querySelector('.node-edit') as HTMLInputElement;
      if (input) {
        input.focus();
        input.select();
      }
    }, 0);
  }

  finishEdit(nodeId: string, newTopic: string): void {
    if (newTopic.trim().length > 0) {
      this.nodeEdit.emit({ id: nodeId, topic: newTopic.trim() });
    }
    this.editingNodeId.set(null);
    // Notificar que edição terminou
    this.editFinished.emit();
    // Selecionar o nó após edição para manter o foco
    this.selectNode(nodeId);
    // Retornar foco ao container para permitir navegação por teclado
    setTimeout(() => {
      const container = document.querySelector('.outline-tree-view') as HTMLElement;
      if (container) {
        container.focus();
      }
    }, 0);
  }

  cancelEdit(): void {
    this.editingNodeId.set(null);
  }

  onNodeKeydown(event: KeyboardEvent, nodeId: string, topic: string): void {
    if (this.disabled()) {
      return;
    }

    if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      this.finishEdit(nodeId, topic);
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      this.cancelEdit();
    }
  }

  private getParentId(nodeId: string): string {
    // Encontrar o nó na estrutura para pegar o parentId
    const findParent = (nodes: TreeNode[], targetId: string, parentId: string = ''): string => {
      for (const node of nodes) {
        if (node.id === targetId) {
          return parentId || node.id;
        }
        if (node.children.length > 0) {
          const found = findParent(node.children, targetId, node.id);
          if (found) return found;
        }
      }
      return '';
    };
    return findParent(this.data(), nodeId);
  }

  isEditing(nodeId: string): boolean {
    return this.editingNodeId() === nodeId;
  }

  getErrorMessage(error: OutlineParseError | null): string {
    if (!error) {
      return '';
    }
    return this.translations().errors[error.code];
  }
}

